import sx from '@fuegokit/react/sx'
import themeGet from '@fuegokit/react/utils/theme-get'
import styled, {keyframes} from 'styled-components'

const checkmarkIn = keyframes`
  from {
    clip-path: inset(16px 0 0 0);
  }

  to {
    clip-path: inset(0 0 0 0);
  }
`

const checkmarkOut = keyframes`
  from {
    clip-path: inset(0 0 0 0);
  }

  to {
    clip-path: inset(16px 0 0 0);
  }
`

export const StyledCheckbox = styled.input`
  appearance: none;
  border-color: ${themeGet('colors.border.default')};
  border-style: solid;
  border-width: ${themeGet('borderWidths.1')};
  background-color: ${themeGet('colors.background.input.default')};
  cursor: pointer;
  display: grid;
  height: 16px;
  margin: 0;
  margin-top: 0.125rem; /* 2px to center align with label (20px line-height) */
  place-content: center;
  position: relative;
  width: 16px;

  &:disabled {
    cursor: not-allowed;
    background-color: ${themeGet('colors.background.disabled')};
    border-color: ${themeGet('colors.border.disabled')};
  }

  border-radius: ${themeGet('radii.1')};
  transition: background-color, border-color 80ms cubic-bezier(0.33, 1, 0.68, 1); /* checked -> unchecked - add 120ms delay to fully see animation-out */

  &::before {
    width: 16px;
    height: 16px;
    visibility: hidden;
    content: '';
    background-color: ${themeGet('colors.text.inverse')};
    transition: visibility 0s linear 230ms;
    clip-path: inset(16px 0 0 0);
    mask-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOSIgdmlld0JveD0iMCAwIDEyIDkiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTEuNzgwMyAwLjIxOTYyNUMxMS45MjEgMC4zNjA0MjcgMTIgMC41NTEzMDUgMTIgMC43NTAzMTNDMTIgMC45NDkzMjEgMTEuOTIxIDEuMTQwMTkgMTEuNzgwMyAxLjI4MUw0LjUxODYgOC41NDA0MkM0LjM3Nzc1IDguNjgxIDQuMTg2ODIgOC43NiAzLjk4Nzc0IDguNzZDMy43ODg2NyA4Ljc2IDMuNTk3NzMgOC42ODEgMy40NTY4OSA4LjU0MDQyTDAuMjAxNjIyIDUuMjg2MkMwLjA2ODkyNzcgNS4xNDM4MyAtMC4wMDMzMDkwNSA0Ljk1NTU1IDAuMDAwMTE2NDkzIDQuNzYwOThDMC4wMDM1NTIwNSA0LjU2NjQzIDAuMDgyMzg5NCA0LjM4MDgxIDAuMjIwMDMyIDQuMjQzMjFDMC4zNTc2NjUgNC4xMDU2MiAwLjU0MzM1NSA0LjAyNjgxIDAuNzM3OTcgNC4wMjMzOEMwLjkzMjU4NCA0LjAxOTk0IDEuMTIwOTMgNC4wOTIxNyAxLjI2MzM0IDQuMjI0ODJMMy45ODc3NCA2Ljk0ODM1TDEwLjcxODYgMC4yMTk2MjVDMTAuODU5NSAwLjA3ODk5MjMgMTEuMDUwNCAwIDExLjI0OTUgMEMxMS40NDg1IDAgMTEuNjM5NSAwLjA3ODk5MjMgMTEuNzgwMyAwLjIxOTYyNVoiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPgo=');
    mask-size: 75%;
    mask-repeat: no-repeat;
    mask-position: center;
    /* color: ${themeGet('colors.text.inverse')}; */

    @media screen and (prefers-reduced-motion: no-preference) {
      animation: ${checkmarkOut} 80ms cubic-bezier(0.65, 0, 0.35, 1) forwards;
    }
  }
  &:checked,
  &:indeterminate {
    background-color: ${themeGet('colors.background.selected.bold.default')};
    border-color: ${themeGet('colors.background.selected.bold.default')};
    &::before {
      @media screen and (prefers-reduced-motion: no-preference) {
        animation: ${checkmarkIn} 80ms cubic-bezier(0.65, 0, 0.35, 1) forwards 80ms;
      }
    }
  }

  &:checked {
    transition: background-color, border-color 80ms cubic-bezier(0.32, 0, 0.67, 0) 0ms;

    &:before {
      visibility: visible;
      transition: visibility 0s linear 0s;
    }

    &:disabled {
      cursor: not-allowed;
      background-color: ${themeGet('colors.background.disabled')};
      border-color: ${themeGet('colors.border.disabled')};
      opacity: 1;

      &:before {
        background-color: ${themeGet('colors.text.disabled')};
      }
    }

    /* Windows High Contrast mode */
    @media (forced-colors: active) {
      background-color: canvastext;
      border-color: canvastext;
    }
  }

  &:indeterminate {
    background: ${themeGet('colors.background.selected.bold.default')};
    &::before {
      mask-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMiIgdmlld0JveD0iMCAwIDEwIDIiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMCAxQzAgMC40NDc3MTUgMC40NDc3MTUgMCAxIDBIOUM5LjU1MjI5IDAgMTAgMC40NDc3MTUgMTAgMUMxMCAxLjU1MjI4IDkuNTUyMjkgMiA5IDJIMUMwLjQ0NzcxNSAyIDAgMS41NTIyOCAwIDFaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K');
      visibility: visible;
    }
  }

  /* fallback :focus state */
  &:focus:not(:disabled) {
    box-shadow: none;
    outline: 2px solid ${themeGet('colors.border.focused')};
    outline-offset: 2px;

    // remove fallback :focus if :focus-visible is supported
    &:not(:focus-visible) {
      outline: solid 1px transparent;
    }
  }

  /* default focus state */
  &:focus-visible:not(:disabled) {
    box-shadow: none;
    outline: 2px solid ${themeGet('colors.border.focused')};
    outline-offset: '2px';
  }
  ${sx}
`
